/**
 * Custom-field validation — Step 28, the shared engine's whole reason to
 * exist. ONE function, run in the browser (as-you-type field errors) and on
 * the server (request validation) — they cannot drift because they are the
 * same code, not two implementations of "what's valid".
 *
 * Not a single static zod object schema: whether a field is even VISIBLE
 * depends on other fields' current values (`showWhen`), so "required" is
 * dynamic. This walks fields IN ORDER — the same order a `showWhen` may only
 * reference backwards into — evaluating visibility as it goes, and only
 * validates what is actually visible. A hidden field's stored value is left
 * untouched, never coerced to empty: switching an answer back later must not
 * have quietly erased what was already answered.
 */
import { z } from 'zod';
import { evaluateCondition } from './conditions.ts';
import type { ResolvedField } from './fieldTypes.ts';

export interface ValidationResult {
  ok: boolean;
  errors: Record<string, string>;
  /** Only the fields actually visible under the current values — the
   *  caller (writeValues on the server) must store exactly this set's
   *  worth of keys, nothing a showWhen hid. */
  visibleKeys: string[];
}

function scalarSchema(rf: ResolvedField): z.ZodTypeAny {
  const { definition: d } = rf;
  const c = d.config;
  switch (d.type) {
    case 'text':
    case 'textarea':
    case 'richtext':
    case 'phone': {
      let s = z.string();
      if (c.minLength !== undefined) s = s.min(c.minLength, `${d.label} is too short`);
      if (c.maxLength !== undefined) s = s.max(c.maxLength, `${d.label} is too long`);
      if (c.pattern)
        s = s.regex(
          new RegExp(c.pattern),
          c.patternMessage || `${d.label} doesn't match the expected format`,
        );
      return s;
    }
    case 'url':
      return z.string().url(`${d.label} must be a valid URL`);
    case 'email':
      return z.string().email(`${d.label} must be a valid email`);
    case 'number': {
      let n = z.number();
      if (c.min !== undefined) n = n.min(c.min);
      if (c.max !== undefined) n = n.max(c.max);
      return n;
    }
    case 'decimal':
      return z.number();
    case 'checkbox':
      return z.boolean();
    case 'date':
    case 'datetime':
      return z.string(); // ISO string — the browser's native input already enforces the shape
    case 'select':
    case 'radio': {
      // Directory-sourced options (peopleFilter) can't be enum-checked here:
      // the option set lives in the directory, not in config.options, and
      // re-resolving it per validation would make validation network-bound.
      // A non-empty string is accepted; the picker itself only offers real
      // directory entries. (Step 61 — an empty enum used to 400 every
      // answer from a peopleFilter dropdown.)
      if (c.peopleFilter) return z.string().min(1);
      const values = (c.options ?? []).filter((o) => !o.archived).map((o) => o.value);
      return c.allowOther ? z.string() : z.enum(values.length ? (values as [string, ...string[]]) : ['']);
    }
    case 'multiselect':
      return z.array(z.string());
    case 'text_list':
      return z.array(z.string().min(1));
    // `id` is null for someone typed in free-text rather than picked from
    // the directory — a new starter's own record often doesn't exist yet,
    // which is exactly the case this field type has to cover.
    case 'person':
      return z.object({ id: z.string().nullable(), name: z.string().min(1), email: z.string().nullable() });
    case 'file': {
      // References, not bytes — the upload already happened and returned an
      // id. Validating the SHAPE matters because these ids are what the
      // server re-points to the new ticket on submit; a malformed entry
      // there would orphan a file silently.
      const one = z.object({
        id: z.string().min(1),
        filename: z.string(),
        contentType: z.string(),
        size: z.number(),
      });
      const max = c.maxFiles ?? 1;
      return z
        .array(one)
        .max(max, max === 1 ? `${d.label} takes one file` : `${d.label} takes at most ${max} files`);
    }
    case 'content':
      return z.never(); // never a value; handled separately below
  }
}

/** Render one stored answer as human-readable text — option VALUES map back
 *  to their labels, people to their names, booleans to Yes/No. Used by the
 *  catalogue submission's message zero (server) and the ticket detail's
 *  answers panel (client, Step 62) — one formatter, so the thread and the
 *  panel can never disagree about what an answer "says". */
export function formatFieldValue(def: import('./fieldTypes.ts').FieldDefinition, value: unknown): string {
  if (value === null || value === undefined || value === '') return '';
  const options = def.config.options ?? [];
  const labelFor = (v: unknown) => options.find((o) => o.value === v)?.label ?? String(v);
  switch (def.type) {
    case 'checkbox':
      return value ? 'Yes' : 'No';
    case 'select':
    case 'radio':
      return labelFor(value);
    case 'multiselect':
      return (Array.isArray(value) ? value : [value]).map(labelFor).join(', ');
    case 'text_list':
      return (Array.isArray(value) ? value : [value]).map(String).join(', ');
    case 'person': {
      const p = value as { name?: string | null; email?: string | null };
      return p?.name || p?.email || '';
    }
    default:
      return String(value);
  }
}

export function validateFieldValues(
  fields: ResolvedField[],
  values: Record<string, unknown>,
): ValidationResult {
  const errors: Record<string, string> = {};
  const visibleKeys: string[] = [];
  const seenSoFar: Record<string, unknown> = {};

  for (const rf of fields) {
    const key = rf.definition.key;
    // showWhen may only reference an EARLIER field — seenSoFar holds exactly
    // that, so a builder mistake that reaches forward just evaluates against
    // "not answered yet" rather than crashing.
    const visible = !rf.showWhen || evaluateCondition(rf.showWhen, seenSoFar);
    seenSoFar[key] = values[key];
    if (rf.definition.type === 'content') continue; // never stored, never validated
    if (!visible) continue;
    visibleKeys.push(key);

    const raw = values[key];
    if (rf.required && (raw === undefined || raw === null || raw === '')) {
      errors[key] = `${rf.definition.label} is required`;
      continue;
    }
    if (raw === undefined || raw === null || raw === '') continue; // optional and empty — fine

    const result = scalarSchema(rf).safeParse(raw);
    if (!result.success) {
      errors[key] = result.error.issues[0]?.message ?? `${rf.definition.label} is invalid`;
    }
  }
  return { ok: Object.keys(errors).length === 0, errors, visibleKeys };
}
